/**
 * M28b — Theme preference.
 *
 * Three-state preference (light / dark / system). Persisted in
 * localStorage; system mode tracks `prefers-color-scheme`.
 *
 * Applied by toggling the `dark` class on `<html>` so Tailwind's
 * `dark:` variant picks it up. `initTheme()` must run BEFORE React
 * renders to avoid a flash-of-light-content on page load.
 */
import { useEffect, useSyncExternalStore } from "react";

export type ThemePref = "light" | "dark" | "system";

const STORAGE_KEY = "hub_theme";

function readStoredPref(): ThemePref {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* private mode / SSR fallback */
  }
  return "system";
}

function systemPrefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyPref(pref: ThemePref): void {
  const effective = pref === "system" ? (systemPrefersDark() ? "dark" : "light") : pref;
  document.documentElement.classList.toggle("dark", effective === "dark");
  document.documentElement.dataset.theme = pref;
}

/**
 * Call this before ReactDOM.createRoot to paint the correct theme on
 * first frame. Safe to call inside a standalone <script> or at the top
 * of main.tsx.
 */
export function initTheme(): void {
  applyPref(readStoredPref());

  // When in `system` mode, react to OS-level changes.
  const mql = window.matchMedia("(prefers-color-scheme: dark)");
  mql.addEventListener("change", () => {
    if (readStoredPref() === "system") applyPref("system");
  });
}

/** Tiny event-bus so useTheme components stay in sync across the tree. */
const listeners = new Set<() => void>();
function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function snapshot(): ThemePref {
  return readStoredPref();
}

export function useTheme(): {
  pref: ThemePref;
  setPref: (next: ThemePref) => void;
  effective: "light" | "dark";
} {
  const systemSnapshot: () => ThemePref = () => "system";
  const pref = useSyncExternalStore<ThemePref>(subscribe, snapshot, systemSnapshot);

  useEffect(() => {
    applyPref(pref);
  }, [pref]);

  const effective: "light" | "dark" =
    pref === "system" ? (systemPrefersDark() ? "dark" : "light") : pref;

  function setPref(next: ThemePref) {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    applyPref(next);
    listeners.forEach((cb) => cb());
  }

  return { pref, setPref, effective };
}
